import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Sparkles,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Bot,
  User as UserIcon,
  Brain,
  Database,
  Layers,
  Calendar,
  Globe,
  FileText,
  Frown,
  GitBranch,
  RotateCw,
  HelpCircle,
} from "lucide-react";

import "./aiAgents.css";

// Keep your existing layout components
import Navbar from "../Navbar/Navbar";
import HowItWorks from "../LandingPage/HowItWorks";
import Customer from "../LandingPage/Customer";
import Footer from "../LandingPage/Footer";

import { useNavigate } from "react-router-dom";

/** ===== Icons mapping for "Scripts vs Agents" ===== */
const Icon = {
  Doc: FileText,
  Sad: Frown,
  Flow: GitBranch,
  Loop: RotateCw,
  PersonQ: HelpCircle,
  Brain: Brain,
  DB: Database,
  Dots: Layers,
  CalPlus: Calendar,
  Globe: Globe,
};

const LEFT_ITEMS = [
  { Icon: Icon.Doc, title: "Fixed replies", text: "Limited to pre-written answers and keywords." },
  { Icon: Icon.Sad, title: "Dead-end loops", text: "Users repeat themselves when the flow misses context." },
  { Icon: Icon.Flow, title: "Branching logic overload", text: "You must pre-design every scenario." },
  { Icon: Icon.Loop, title: "Low adaptability", text: "Breaks on new questions and edge cases." },
  { Icon: Icon.PersonQ, title: "Shallow resolution", text: "Struggles with multi-step or ambiguous requests." },
];

const RIGHT_ITEMS = [
  { Icon: Icon.Brain, title: "Context-aware reasoning", text: "Understands intent instead of matching keywords." },
  { Icon: Icon.DB, title: "Knowledge + tool access", text: "Uses your docs and connects to systems via APIs." },
  { Icon: Icon.Dots, title: "Personalized handling", text: "Adjusts responses to the user's situation." },
  { Icon: Icon.CalPlus, title: "Task completion", text: "Can schedule, update records, create tickets, and more." },
  { Icon: Icon.Globe, title: "Global-ready", text: "Supports 50+ languages across channels." },
];

const CHECK_ITEMS = [
  { title: "Plug-in integrations", text: "Connect to platforms like CRMs and helpdesks using secure APIs." },
  { title: "Train with natural language", text: "Teach the agent using your existing content, like onboarding a teammate." },
  { title: "Data boundaries & access", text: "Control what the agent can read, use, and update." },
  { title: "Workflow execution", text: "Automate multi-step processes with approvals and human handoff." },
];

const SOURCE_LOGOS = {
  drive: "https://cdn.simpleicons.org/googledrive/34A853",
  confluence: "https://cdn.simpleicons.org/confluence/0052CC",
  notion: "https://cdn.simpleicons.org/notion/000000",
  salesforce: "https://api.iconify.design/simple-icons:salesforce.svg?color=%2300A1E0",
  wordpress: "https://cdn.simpleicons.org/wordpress/21759B",
  pdf: "https://upload.wikimedia.org/wikipedia/commons/8/87/PDF_file_icon.svg",
  gdocs: "https://cdn.simpleicons.org/googledocs/4285F4",
};

const INDUSTRY_CARDS = [
  {
    title: "Human Resources",
    body: "Help employees with policies, leave balance FAQs, onboarding checklists, and internal requests — with routing to HR when needed.",
    gradient: "from-purple-500 to-purple-600",
  },
  {
    title: "E-commerce",
    body: "Answer order questions, initiate returns, share delivery updates, and guide product discovery based on catalog and policy pages.",
    gradient: "from-blue-500 to-cyan-600",
  },
  {
    title: "Healthcare",
    body: "Conduct initial patient triage based on symptoms, route to suitable care, manage appointments, and deliver essential health information.",
    gradient: "from-green-500 to-emerald-600",
  },
  {
    title: "Education",
    body: "Support admissions FAQs, course details, fees, deadlines, and application steps with information pulled from verified sources.",
    gradient: "from-orange-500 to-red-600",
  },
  {
    title: "Retail",
    body: "Check stock, process payments, manage returns, enroll customers in loyalty programs, and inform them about special promotions.",
    gradient: "from-pink-500 to-rose-600",
  },
  {
    title: "Customer Support",
    body: "Handle 70%+ of inquiries—account updates to subscription management. Seamlessly escalate complex issues to humans.",
    gradient: "from-indigo-500 to-purple-600",
  },
];

export default function AiAgents() {
  const navigate = useNavigate();

  return (
    <>
      <Navbar />

      <div className="ai-agents-page">
        {/* ===== HERO ===== */}
        <section className="agents-hero">
          <div className="agents-hero-container">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="agents-hero-badge">
              <Sparkles className="badge-icon" />
              <span>Next-Generation AI Agents</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="agents-hero-title"
            >
              AI Agents built for real workflows,
              <br className="hide-mobile" />
              not scripted conversations
              <br />
              <span className="title-gradient">Work like a teammate</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="agents-hero-description"
            >
              Train on your site + docs, set rules/guardrails, and deploy across web + messaging. When a request needs action,
              XenaChat can collect details, route it, and notify your team.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="agents-hero-buttons"
            >
              <button className="btn-primary" onClick={() => navigate("/dashboard")}>
                <Sparkles className="btn-icon" />
                <span>Start building</span>
              </button>

              <button className="btn-secondary" onClick={() => navigate("/bookdemo")}>
                <span>Book a Demo</span>
                <ArrowRight className="btn-icon" />
              </button>
            </motion.div>

            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="agents-hero-note">
              Setup in minutes • Cancel anytime
            </motion.p>
          </div>
        </section>

        {/* ===== WHY (Scripts vs Agents) ===== */}
        <section className="agents-why-section">
          <div className="agents-why-container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="agents-why-header"
            >
              <h2 className="agents-why-title">
                <span className="title-gradient">Scripts vs Agents:</span>
                <br />
                Why The New Approach Works Better
              </h2>

              <p className="agents-why-subtitle">
                Traditional bots follow fixed paths. XenaChat Agents adapt to the request, use your connected knowledge, and complete the next
                step. <strong>Built on XenaChat&apos;s decision layer for reliable handling.</strong>
              </p>
            </motion.div>

            <div className="agents-why-grid">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="agents-why-card traditional"
              >
                <div className="card-header">
                  <XCircle className="header-icon traditional-icon" />
                  <h3>Traditional Chatbots</h3>
                </div>

                <ul className="card-list">
                  {LEFT_ITEMS.map(({ Icon: Ico, title, text }) => (
                    <li key={title} className="card-item">
                      <div className="item-icon traditional-item">
                        <Ico className="icon" />
                      </div>
                      <div className="item-content">
                        <div className="item-title">{title}</div>
                        <div className="item-text">{text}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="agents-why-card modern"
              >
                <div className="card-header">
                  <CheckCircle2 className="header-icon modern-icon" />
                  <h3>AI Agents</h3>
                </div>

                <ul className="card-list">
                  {RIGHT_ITEMS.map(({ Icon: Ico, title, text }) => (
                    <li key={title} className="card-item">
                      <div className="item-icon modern-item">
                        <Ico className="icon" />
                      </div>
                      <div className="item-content">
                        <div className="item-title">{title}</div>
                        <div className="item-text">{text}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ===== TRAIN HERO ===== */}
        <section className="agents-train-section">
          <div className="agents-train-container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="agents-train-content"
            >
              <h2 className="agents-train-title">
                Go Live Fast:
                <br />
                <span className="title-gradient">Train and Deploy in Hours, not Weeks</span>
              </h2>
              <p className="agents-train-subtitle">
                Connect your sources, configure behavior, and launch across your channels without complex implementation.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ===== AUTOMATE ===== */}
        <AutomateSection />

        {/* ===== SOURCES ===== */}
        <SourcesSection />

        {/* ===== HANDLE / HANDOFF ===== */}
        <HandleSection />

        {/* ===== INDUSTRY ===== */}
        <IndustryUseCasesSection />
      </div>

      {/* keep your other landing sections */}
      <HowItWorks />
      <Customer />
      <Footer />
    </>
  );
}

/* -------------------- Automate Section -------------------- */
function AutomateSection() {
  const [visibleMessages, setVisibleMessages] = useState(0);
  const [isInView, setIsInView] = useState(false);

  const chatMessages = [
    { id: 1, who: "user", text: "Can I reschedule my flight to next Monday?" },
    { id: 2, who: "bot", text: "Let me check the availability for next Monday…" },
    {
      id: 3,
      who: "bot",
      text: "Great, you are eligible for a reschedule. Here's a list of available flights on Monday. Please pick one:",
      options: true,
    },
    { id: 4, who: "bot", text: "Great. Let me confirm that for you." },
    {
      id: 5,
      who: "bot",
      text: "Your flight has been rescheduled to next Monday. Confirmation and details have been sent to your email.",
      metadata: true,
    },
    { id: 6, who: "user", text: "Thank you very much! ❤" },
  ];

  useEffect(() => {
    if (isInView && visibleMessages < chatMessages.length) {
      const t = setTimeout(() => setVisibleMessages((p) => p + 1), 1000);
      return () => clearTimeout(t);
    }
  }, [visibleMessages, isInView, chatMessages.length]);

  return (
    <section className="agents-automate-section">
      <div className="agents-automate-container">
        <div className="agents-automate-grid">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="agents-automate-content"
          >
            <h3 className="agents-automate-title">
              <span className="title-gradient">Automate</span>
              <br />
              Repeat Requests End-to-End
            </h3>

            <p className="agents-automate-intro">
              XenaChat Agents don&apos;t stop at answering, they can collect details, validate information, and trigger the next step in your workflow.
            </p>

            <ul className="agents-checklist">
              {CHECK_ITEMS.map((item) => (
                <li key={item.title} className="check-item">
                  <div className="check-icon">
                    <CheckCircle2 className="icon" />
                  </div>
                  <div className="check-content">
                    <div className="check-title">{item.title}</div>
                    <div className="check-text">{item.text}</div>
                  </div>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-120px" }}
            onViewportEnter={() => {
              if (!isInView) {
                setIsInView(true);
                setVisibleMessages(1);
              }
            }}
            className="agents-automate-chat"
          >
            <div className="chat-card">
              <div className="chat-header">
                <div className="chat-agent">
                  <div className="agent-avatar">
                    <Bot className="agent-icon" />
                  </div>
                  <div className="agent-info">
                    <div className="agent-name">Flight Assistant</div>
                    <div className="agent-status">
                      <span className="status-dot" />
                      <span>AI Agent</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="chat-messages">
                <AnimatePresence>
                  {chatMessages.slice(0, visibleMessages).map((msg) => (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 20, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ duration: 0.35 }}
                      className={`chat-message ${msg.who}`}
                    >
                      {msg.who === "bot" && (
                        <div className="message-avatar bot">
                          <Bot className="avatar-icon" />
                        </div>
                      )}

                      <div className={`message-bubble ${msg.who}`}>
                        <p>{msg.text}</p>

                        {msg.options && (
                          <div className="flight-options">
                            <div className="flight-option">
                              <div className="flight-time">4:00 PM</div>
                              <div className="flight-route">MNL • Direct</div>
                            </div>
                            <div className="flight-option">
                              <div className="flight-time">9:30 PM</div>
                              <div className="flight-route">CTS</div>
                            </div>
                          </div>
                        )}

                        {msg.metadata && (
                          <div className="message-meta">
                            <CheckCircle2 className="meta-icon" />
                            Confirmed • Ticket #5931
                          </div>
                        )}
                      </div>

                      {msg.who === "user" && (
                        <div className="message-avatar user">
                          <UserIcon className="avatar-icon" />
                        </div>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>

                {visibleMessages < chatMessages.length && visibleMessages > 0 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="typing-indicator">
                    <div className="message-avatar bot">
                      <Bot className="avatar-icon" />
                    </div>
                    <div className="typing-dots">
                      <span />
                      <span />
                      <span />
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Sources Section -------------------- */
function SourcesSection() {
  return (
    <section className="agents-sources-section">
      <div className="agents-sources-container">
        <div className="agents-sources-grid">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="agents-sources-visual"
          >
            <div className="orbit-container">
              <div className="orbit-ring ring-1" />
              <div className="orbit-ring ring-2" />
              <div className="orbit-ring ring-3" />

              <div className="orbit-center">
                <div className="center-icon">
                  <Brain className="brain-icon" />
                </div>
              </div>

              <div className="orbit-badge badge-1">
                <img src={SOURCE_LOGOS.drive} alt="Google Drive" />
              </div>
              <div className="orbit-badge badge-2">
                <img src={SOURCE_LOGOS.confluence} alt="Confluence" />
              </div>
              <div className="orbit-badge badge-3">
                <img src={SOURCE_LOGOS.notion} alt="Notion" />
              </div>
              <div className="orbit-badge badge-4">
                <img src={SOURCE_LOGOS.salesforce} alt="Salesforce" />
              </div>
              <div className="orbit-badge badge-5">
                <img src={SOURCE_LOGOS.wordpress} alt="WordPress" />
              </div>
              <div className="orbit-badge badge-6">
                <img src={SOURCE_LOGOS.pdf} alt="PDF" />
              </div>
              <div className="orbit-badge badge-7">
                <img src={SOURCE_LOGOS.gdocs} alt="Google Docs" />
              </div>
              <div className="orbit-badge badge-8">
                <Globe className="globe-icon" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="agents-sources-content"
          >
            <h3 className="agents-sources-title">
              Understands Your Content <span className="title-gradient">Across Formats</span> and Sources
            </h3>

            <ul className="agents-sources-list">
              <li>
                <div className="list-dot" />
                <div>
                  <strong>Wide Source Support:</strong> Websites, FAQs, PDFs, docs, spreadsheets, and knowledge bases.
                </div>
              </li>
              <li>
                <div className="list-dot" />
                <div>
                  <strong>Sync Updates:</strong> Keep information current by refreshing connected sources.
                </div>
              </li>
              <li>
                <div className="list-dot" />
                <div>
                  <strong>Built-in Safeguards:</strong> Reduce errors with guardrails and structured retrieval.
                </div>
              </li>
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Handle/Handoff Section -------------------- */
function HandleSection() {
  const [visibleMessages, setVisibleMessages] = useState(0);
  const [isInView, setIsInView] = useState(false);

  const handoffMessages = [
    {
      id: 1,
      who: "user",
      text: "It's the charge dated April 15th. I was billed twice for the same service.",
    },
    {
      id: 2,
      who: "bot",
      text: "I see the issue you're mentioning, but I'll need to hand you over to our billing specialist who can adjust this for you.",
    },
    { id: 3, who: "handoff", agent: "Sarah" },
    {
      id: 4,
      who: "agent",
      text: "Hi Alex, I'm Sarah, your billing specialist. I see the duplicate charge here. I'll process a refund for the overcharge right away. Is there anything else I can assist you with today?",
    },
  ];

  useEffect(() => {
    if (isInView && visibleMessages < handoffMessages.length) {
      const t = setTimeout(() => setVisibleMessages((p) => p + 1), 1100);
      return () => clearTimeout(t);
    }
  }, [visibleMessages, isInView, handoffMessages.length]);

  return (
    <section className="agents-handle-section">
      <div className="agents-handle-container">
        <div className="agents-handle-grid">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="agents-handle-content"
          >
            <h3 className="agents-handle-title">
              Handle Most Inquiries Automatically
              <br />
              and Route The Rest Smoothly
              <br />
              <span className="title-gradient">Over 70%</span> Of Them
            </h3>

            <p className="agents-handle-subtitle">A practical balance of automation and human support.</p>

            <ul className="agents-handle-list">
              <li>
                <div className="list-dot" />
                <div>
                  <strong>High Coverage:</strong> Resolves the majority of common questions without agent involvement.
                </div>
              </li>
              <li>
                <div className="list-dot" />
                <div>
                  <strong>Smart Escalation:</strong> Detects when a human is needed (policy exceptions, frustration, sensitive cases).
                </div>
              </li>
              <li>
                <div className="list-dot" />
                <div>
                  <strong>Seamless Handoff:</strong> Passes context to tools like helpdesks/CRMs so users don&apos;t repeat themselves.
                </div>
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-120px" }}
            onViewportEnter={() => {
              if (!isInView) {
                setIsInView(true);
                setVisibleMessages(1);
              }
            }}
            className="agents-handle-demo"
          >
            <div className="handoff-card">
              <div className="handoff-messages">
                <AnimatePresence>
                  {handoffMessages.slice(0, visibleMessages).map((msg) => {
                    if (msg.who === "handoff") {
                      return (
                        <motion.div
                          key={msg.id}
                          initial={{ opacity: 0, scale: 0.92 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.4 }}
                          className="handoff-transition"
                        >
                          <div className="transition-avatar">
                            <UserIcon className="avatar-icon" />
                          </div>
                          <div className="transition-text">
                            You are now connected with <strong>{msg.agent}</strong>
                          </div>
                        </motion.div>
                      );
                    }

                    return (
                      <motion.div
                        key={msg.id}
                        initial={{ opacity: 0, y: 18, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.35 }}
                        className={`handoff-message ${msg.who}`}
                      >
                        {msg.who === "bot" && (
                          <div className="message-avatar bot">
                            <Bot className="avatar-icon" />
                          </div>
                        )}
                        {msg.who === "agent" && (
                          <div className="message-avatar agent">
                            <UserIcon className="avatar-icon" />
                          </div>
                        )}

                        <div className={`message-bubble ${msg.who}`}>
                          <p>{msg.text}</p>
                        </div>

                        {msg.who === "user" && (
                          <div className="message-avatar user">
                            <UserIcon className="avatar-icon" />
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </AnimatePresence>

                {visibleMessages < handoffMessages.length && visibleMessages > 0 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="typing-indicator">
                    <div className="message-avatar bot">
                      <Bot className="avatar-icon" />
                    </div>
                    <div className="typing-dots">
                      <span />
                      <span />
                      <span />
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Industry Section -------------------- */
function IndustryUseCasesSection() {
  return (
    <section className="agents-industry-section">
      <div className="agents-industry-container">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="agents-industry-header"
        >
          <h2 className="agents-industry-title">
            Where teams deploy <span className="title-gradient">XenaChat Agents</span>
          </h2>
          <p className="agents-industry-subtitle">
            Built for real operations not just Q&amp;A. Use agents to support customers, employees, and partners.
          </p>
        </motion.div>

        <div className="agents-industry-grid">
          {INDUSTRY_CARDS.map((card, index) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08 }}
              className="industry-card"
            >
              <div className={`industry-card-gradient bg-gradient-to-br ${card.gradient}`} />
              <div className="industry-card-content">
                <h3 className="industry-card-title">{card.title}</h3>
                <p className="industry-card-body">{card.body}</p>
              </div>
              <div className="industry-card-icon">
                <ArrowRight className="icon" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
